import { type Theme } from '@mui/material';

// How the prototype is sized on a stage: in the device mockup ('frame'), filling
// the stage ('fill'), or pinned to a fixed pixel width so MUI's responsive
// breakpoints actually engage (a number).
export type Viewport = 'frame' | 'fill' | number;

// The laptop's logical screen width, and what a web stage (CAM) opens on. The
// size is the useful part of the mockup — it is the width these screens are
// actually designed against — and the Cherry bezel is a marketing prop, so the
// default is the width without the laptop drawn around it. ComputerFrame reads
// this too, so the framed and unframed views are the same screen.
export const computerScreenWidth = 1440;

// The height that goes with it (16:10). A fixed-width stage uses this as a floor
// rather than taking whatever is left under the stage chrome: a web design read
// in a 600px-tall slot is a different design, and the answer to a tall page is
// to scroll the stage, not to squash the screen.
export const computerScreenHeight = 900;

// The width options, aligned to the MUI breakpoints (read live from the theme so
// they can't drift), plus a sensible mobile width and the laptop screen above.
// 'frame'/'fill' lead.
export const viewportWidthOptions = (
  theme: Theme,
): { width: number; label: string }[] => {
  const { sm, md, lg, xl } = theme.breakpoints.values;
  return [
    { width: 375, label: '375 · mobile' },
    { width: sm, label: `${sm} · sm` },
    { width: md, label: `${md} · md` },
    { width: lg, label: `${lg} · lg` },
    { width: computerScreenWidth, label: `${computerScreenWidth} · laptop` },
    { width: xl, label: `${xl} · xl` },
  ];
};

export const viewportToParam = (v: Viewport): string =>
  typeof v === 'number' ? String(v) : v;

export const viewportFromParam = (raw: string | null): Viewport => {
  if (raw === 'fill') return 'fill';
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 'frame';
};
